import React, { useState, useEffect, useCallback } from 'react';

function UserManagement({ token }) {
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingUser, setEditingUser] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [formData, setFormData] = useState({ username: '', password: '', roles: 'ROLE_USER' });

  const fetchUsers = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await fetch('/api/users', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Failed to fetch users');
      const data = await response.json();
      setUsers(data);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const resetForm = () => {
    setFormData({ username: '', password: '', roles: 'ROLE_USER' });
    setEditingUser(null);
    setIsCreating(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const url = editingUser ? `/api/users/${editingUser.id}` : '/api/users';
    const method = editingUser ? 'PUT' : 'POST';
    
    const body = {
      username: formData.username,
      roles: formData.roles.split(',').map(r => r.trim())
    };
    if (formData.password) {
      body.password = formData.password;
    }

    try {
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(body)
      });

      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.error || 'Operation failed');
      }

      resetForm();
      fetchUsers();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    setIsCreating(false);
    setFormData({
      username: user.username,
      password: '',
      roles: user.roles.join(', ')
    });
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;
    try {
      const response = await fetch(`/api/users/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Failed to delete user');
      fetchUsers();
    } catch (err) {
      setError(err.message);
    }
  };

  if (isLoading && users.length === 0) return <div className="mt-4">Loading users...</div>;

  return (
    <div className="card shadow-sm mt-4">
      <div className="card-header bg-primary text-white d-flex justify-content-between align-items-center">
        <h5 className="mb-0">User Management</h5>
        <button 
          className="btn btn-light btn-sm" 
          onClick={() => { setIsCreating(true); setEditingUser(null); setFormData({ username: '', password: '', roles: 'ROLE_USER' }); }}
        >
          Add User
        </button>
      </div>
      <div className="card-body">
        {error && <div className="alert alert-danger">{error}</div>}

        {(isCreating || editingUser) && (
          <div className="border rounded p-3 mb-4 bg-light">
            <h6>{editingUser ? 'Edit User' : 'Create New User'}</h6>
            <form onSubmit={handleSubmit}>
              <div className="row g-3">
                <div className="col-md-4">
                  <input
                    type="text"
                    name="username"
                    className="form-control form-control-sm"
                    placeholder="Username"
                    value={formData.username}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="col-md-4">
                  <input
                    type="password"
                    name="password"
                    className="form-control form-control-sm"
                    placeholder={editingUser ? "New Password (optional)" : "Password"}
                    value={formData.password}
                    onChange={handleInputChange}
                    required={!editingUser}
                  />
                </div>
                <div className="col-md-4">
                  <input
                    type="text"
                    name="roles"
                    className="form-control form-control-sm"
                    placeholder="Roles (comma separated)"
                    value={formData.roles}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="col-12">
                  <button type="submit" className="btn btn-success btn-sm me-2">Save</button>
                  <button type="button" className="btn btn-secondary btn-sm" onClick={resetForm}>Cancel</button>
                </div>
              </div>
            </form>
          </div>
        )}

        <div className="table-responsive">
          <table className="table table-striped table-hover">
            <thead>
              <tr>
                <th>ID</th>
                <th>Username</th>
                <th>Roles</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => (
                <tr key={user.id}>
                  <td>{user.id}</td>
                  <td>{user.username}</td>
                  <td>{user.roles.join(', ')}</td>
                  <td>
                    <button className="btn btn-outline-primary btn-sm me-2" onClick={() => handleEdit(user)}>Edit</button>
                    <button className="btn btn-outline-danger btn-sm" onClick={() => handleDelete(user.id)}>Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default UserManagement;
